import fileSize from 'filesize';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { RootState } from '../../../store';
import SusyCheckbox from '../forms/SusyCheckbox';
import SusyFileBox from '../SusyFileBox/SusyFileBox';

import './FileItem.css';

interface IFileItemProps {
    idPrefix?: string; //This is used if multiple FileItems are used on page, so we can select the one we clicked
    name: string | undefined;
    size: number;
    index: number;
    onRemoveExistingDocs?: () => void;
    onDownloadFile?: () => void;
    isMainFile?: boolean;
    isPresentationFile?: boolean;
    toggleMainFile?: () => void;
    togglePresentationFile?: () => void;
    hidePresentationFile?: boolean;
}

const FileItem = (props: IFileItemProps) => {
    const isPdfFile = props.name?.endsWith('.pdf');
    const isEligibleForPresentation =
        props.name?.endsWith('.pdf') ||
        props.name?.endsWith('.ppt') ||
        props.name?.endsWith('.pptx');
    const localization = useSelector((state: RootState) => state.localization)
        .localizationStrings.susy.shared.fileItem.handInFileItem;

    const getPresentationOverlay = () => {
        if (isEligibleForPresentation && props.togglePresentationFile) {
            return <></>;
        }

        if (!props.togglePresentationFile) {
            return <Tooltip>{localization.presentationNotAvailable}</Tooltip>;
        }

        return <Tooltip>{localization.notPresentationMessage}</Tooltip>;
    };

    return (
        <div
            className={`susy-uploaded-file-item-container mb-4`}
            data-testid={`file-item-${props.index}`}
            key={props.index}
        >
            <SusyFileBox
                fileName={props.name ?? ''}
                description={`${localization.size}: ${fileSize(
                    props.size || 0
                )}`}
                onDownloadFile={props.onDownloadFile}
                onRemoveFile={props.onRemoveExistingDocs}
                dataTestId={`file-item-file-box-${props.index}`}
            />
            {(props.toggleMainFile || !props.hidePresentationFile) && (
                <div className='project-files-checkboxes d-flex mt-3 flex-column flex-md-row'>
                    {props.toggleMainFile && (
                        <OverlayTrigger
                            placement='bottom'
                            overlay={
                                isPdfFile ? (
                                    <></>
                                ) : (
                                    <Tooltip id='button-tooltip-3'>
                                        {localization.notPdfMessage}
                                    </Tooltip>
                                )
                            }
                        >
                            <div
                                className={`mr-4 ${
                                    !isPdfFile ? 'cb-disabled' : ''
                                } `}
                            >
                                <SusyCheckbox
                                    label={localization.mainProject}
                                    fieldIdentifier={`${
                                        props.idPrefix ?? ''
                                    }file-item-${props.index}-${name}`}
                                    onChange={() => {
                                        if (isPdfFile && props.toggleMainFile) {
                                            props.toggleMainFile?.();
                                        }
                                    }}
                                    checked={props.isMainFile}
                                    dataTestId={`file-item-main-file-cb-${props.index}`}
                                />
                            </div>
                        </OverlayTrigger>
                    )}
                    {!props.hidePresentationFile && (
                        <OverlayTrigger
                            placement='bottom'
                            overlay={getPresentationOverlay()}
                        >
                            <SusyCheckbox
                                label={localization.presentationFile}
                                disabled={
                                    !isEligibleForPresentation ||
                                    !props.togglePresentationFile
                                }
                                fieldIdentifier={`${
                                    props.idPrefix ?? ''
                                }file-item-presentation-${props.index}-${name}`}
                                onChange={() => {
                                    if (
                                        isEligibleForPresentation &&
                                        props.togglePresentationFile
                                    ) {
                                        props.togglePresentationFile();
                                    }
                                }}
                                checked={props.isPresentationFile}
                                dataTestId={`file-item-presentation-file-cb-${props.index}`}
                            />
                        </OverlayTrigger>
                    )}
                </div>
            )}
        </div>
    );
};

export default FileItem;
